import { flushSync } from "react-dom"
import { buildMissionChange } from "./missionChange"

/**
 * setState for the App's updateStateFor* helpers, committed at once.
 *
 * They were written for React 16, which committed a setState made outside a React
 * event straight away, and each builds its next state from component.state. createRoot
 * batches those, so two in a row (socket events, promise callbacks) built on the same
 * stale state and the second undid the first. flushSync gives them back the commit
 * they were written for.
 *
 * From inside a setState callback (React is mid-commit and can't flush) it is a plain
 * setState: React flushes it at the end of that same commit anyway.
 *
 * @param component {React.Component} The class component whose state is set (the App)
 */
export function commitState(component, partial, cb) {
    // Callbacks run mid-commit: anything they commitState in turn takes the plain path
    const callback = cb
        ? () => {
              component.stateCallbackDepth = (component.stateCallbackDepth || 0) + 1
              try {
                  cb()
              } finally {
                  component.stateCallbackDepth--
              }
          }
        : undefined

    if (component.stateCallbackDepth) {
        component.setState(partial, callback)
        return
    }

    flushSync(() => {
        component.setState(partial, callback)
    })
}

/**
 * One socket message of other tabs' changes, applied in one commit with one missionChange
 * notice (utils/missionChange.js). The App's socket handlers all land here.
 *
 * An updater, committed at once like the helpers (commitState above), so it builds on the
 * latest state and nothing reading component.state after it sees the old missions.
 *
 * @param component {React.Component} The App
 * @param reduce {(prevState) => { entries: object[], [stateKey]: any }} A pure reducer
 *   (utils/actionItemTreeState.js, utils/missionTreeSocketState.js). Of what it returns,
 *   the keys in stateKeys that changed are committed, with the entries as one notice.
 * @param stateKeys {string[]} State keys the reducer may return
 * @param [after] {(applied) => void} Runs after the commit, when anything was applied
 * @param [extraState] {(applied) => object} More state for the same commit
 */
export function applySocketMessage(component, reduce, stateKeys, after, extraState) {
    let applied

    commitState(
        component,
        (prevState) => {
            applied = reduce(prevState)

            const next = {}

            stateKeys.forEach((key) => {
                if (applied[key] !== prevState[key]) next[key] = applied[key]
            })

            if (applied.entries.length) {
                next.missionChange = buildMissionChange(applied.entries)
            }

            if (!Object.keys(next).length) {
                applied = null
                return null
            }

            return { ...next, ...(extraState ? extraState(applied) : {}) }
        },
        () => {
            if (applied && after) after(applied)
        }
    )
}
